import classNames from 'classnames';
import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import { Component } from 'react';
import Icon from '/@/components/icon';
import { getCornPlantingDensity } from '/@/core/density';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import Input from '/@/v3/components/forms/input';
import styles from './styles.module.css';

@autobind
export default class CornPlantingDensityCalculator extends Component {
    static contextTypes = {
        units: PropTypes.object,
        print: PropTypes.bool,
    };

    state = {
        collapsed: false,
    };

    UNSAFE_componentWillMount() {
        this.setState({ collapsed: !!this.props.collapsed });
        this.units = this.context.units;
        this.linearUnits = this.units.clone({ cornDensity: 'm' });
    }

    handleOpen(event) {
        event.stopPropagation();
        this.setState({ collapsed: false });
        this.props.onUserCollapsedChange(false);
    }

    handleClose(event) {
        event.stopPropagation();
        this.setState({ collapsed: true });
        this.props.onUserCollapsedChange(true);
    }

    getLocaleValue(obj, key) {
        return (obj && obj[key] && obj?.[key]?.localeValue) || undefined;
    }

    getDensity(results) {
        return this.getLocaleValue(results, 'density') || this.renderNotAvailable();
    }

    getLinearDensity(results) {
        return this.getLocaleValue(results, 'linearDensity') || this.renderNotAvailable();
    }

    getBagsPerArea(results) {
        return this.getLocaleValue(results, 'bagsPerArea') || this.renderNotAvailable();
    }

    getBagsWithTech(results) {
        return this.getLocaleValue(results, 'bagsWithTech') || this.renderNotAvailable();
    }

    getBagsWithoutTech(results) {
        return this.getLocaleValue(results, 'bagsWithoutTech') || this.renderNotAvailable();
    }

    getTotal(results) {
        return this.getLocaleValue(results, 'total') || this.renderNotAvailable();
    }

    calculate({ profile: { product }, harvestDensity }) {
        const { units, linearUnits } = this;

        const results = getCornPlantingDensity(
            harvestDensity.recommended,
            this.props.userPlanterEfficiency,
            product.seedsPerBag,
            units
        );

        if (!results) return;

        const density = units.toCornDensity(results.density, undefined, 'planting');
        const linearDensity = linearUnits.toCornDensity(results.density, 2, 'planting');

        const bagsPerArea = units.toBags(results.bagsPerHectare, 2);

        const area = units.toArea(this.props.userArea, 2);
        const total = area ? units.toBagsConverted(Math.round(bagsPerArea.value * area.value)) : undefined;
        const bagsWithTech = total ? units.toBagsConverted(Math.round(0.9 * total.value)) : undefined;
        const bagsWithoutTech = total ? units.toBagsConverted(Math.round(total.value - bagsWithTech.value)) : undefined;

        return {
            density,
            linearDensity,
            bagsPerArea,
            bagsWithTech,
            bagsWithoutTech,
            total,
        };
    }

    renderLauncher() {
        const { print } = this.context;
        const { collapsed } = this.state;

        return (
            <div className={styles.launcher}>
                <div
                    className={classNames(styles.title, styles[`theme-${settings.app.theme}`])}
                    onClick={this.handleOpen}
                >
                    <Icon type='cornCalculator' size='sm' theme={settings.app.theme} />
                    <span>{collapsed ? _('Calcular densidad a la siembra') : _('Densidad a la siembra')}</span>
                </div>
                {!print && !collapsed && (
                    <div className={styles.close} onClick={this.handleClose}>
                        <i className='material-icons'>close</i>
                    </div>
                )}
            </div>
        );
    }

    renderConfiguration() {
        if (this.state.collapsed) return null;

        return (
            <div className={styles.configuration}>
                <div className={styles.fields}>
                    <div className={styles.field}>
                        <div className={styles.label}>{_('Porcentaje de logro')}</div>
                        <div className={styles.input}>
                            <Input
                                type='number'
                                color={settings.app.theme}
                                className={styles.input}
                                suffix='%'
                                value={this.props.userPlanterEfficiency}
                                onChange={this.props.onUserPlanterEfficiencyChange}
                                disabled={this.props.status === 'read_only'}
                            />
                        </div>
                    </div>
                    <div className={styles.field}>
                        <div className={styles.label}>{_('Superficie')}</div>
                        <div className={styles.input}>
                            <Input
                                type='number'
                                color={settings.app.theme}
                                className={styles.input}
                                suffix={this.units.getUnit('area')}
                                onChange={this.props.onUserAreaChange}
                                value={
                                    this.props.userArea !== ''
                                        ? this.units.toArea(this.props.userArea, 'corn').value
                                        : ''
                                }
                                disabled={this.props.status === 'read_only'}
                            />
                        </div>
                    </div>
                </div>
                <div className={styles.notes}>
                    {_('Separación entre líneas')}: {this.props.brand.cropSpacing}m
                    <br />
                    <br />
                    {_('Cálculos hechos en base a la densidad recomendada.')}
                </div>
            </div>
        );
    }

    renderNotAvailable() {
        return <span className={styles.notAvailable}>-</span>;
    }

    renderMobile({ selectedProductRecommendation }) {
        if (this.state.collapsed) return null;

        const results = this.calculate(selectedProductRecommendation);

        return (
            <div className={styles.rootMobile}>
                <table className={styles.table}>
                    <tbody>
                        <tr className={styles.featured}>
                            <td>{this.linearUnits.getUnit('density', 'corn', 'planting')}</td>
                            <td className={styles.results}>{this.getLinearDensity(results)}</td>
                        </tr>
                        <tr>
                            <td>{this.units.getUnit('density', 'corn')}</td>
                            <td className={styles.results}>{this.getDensity(results)}</td>
                        </tr>
                        <tr>
                            <td>
                                {_('bolsas')}/{this.units.getUnit('area')}
                            </td>
                            <td className={styles.results}>{this.getBagsPerArea(results)}</td>
                        </tr>
                        <tr>
                            <td>{_('bolsas totales con \n tecnología bt')}</td>
                            <td className={styles.results}>{this.getBagsWithTech(results)}</td>
                        </tr>
                        <tr>
                            <td>{_('bolsas totales sin \n tecnología bt')}</td>
                            <td className={styles.results}>{this.getBagsWithoutTech(results)}</td>
                        </tr>
                        <tr className={styles.featured}>
                            <td>{_('bolsas totales')}</td>
                            <td className={styles.results}>{this.getTotal(results)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    }

    renderDesktop({ productRecommendations }) {
        if (this.state.collapsed) return null;

        return (
            <div className={styles.rootDesktop}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th />
                            <th className={styles.featured}>
                                {this.linearUnits.getUnit('density', 'corn', 'planting')}
                            </th>
                            <th>{this.units.getUnit('density', 'corn', 'planting')}</th>
                            <th>
                                {_('bolsas')}/{this.units.getUnit('area')}
                            </th>
                            <th>{_('bolsas totales con \n tecnología bt')}</th>
                            <th>{_('bolsas totales sin \n tecnología bt')}</th>
                            <th className={styles.featured}>{_('bolsas totales')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {productRecommendations.map((productRecommendation) => {
                            const results = this.calculate(productRecommendation);
                            return (
                                <tr key={productRecommendation.profile.id}>
                                    <td>{productRecommendation.profile.label}</td>
                                    <td className={styles.featured}>{this.getLinearDensity(results)}</td>
                                    <td>{this.getDensity(results)}</td>
                                    <td>{this.getBagsPerArea(results)}</td>
                                    <td>{this.getBagsWithTech(results)}</td>
                                    <td>{this.getBagsWithoutTech(results)}</td>
                                    <td className={styles.featured}>{this.getTotal(results)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    render() {
        const { productRecommendations, selectedProductRecommendation, userPlanterEfficiency, userArea } = this.props;
        if (this.context.print && (!userPlanterEfficiency || !userArea)) return null;

        return (
            <div
                className={classNames(styles.root, styles[`theme-${settings.app.theme}`], {
                    [styles.collapsed]: this.state.collapsed,
                })}
            >
                {this.renderLauncher()}
                {this.renderConfiguration()}
                {this.renderMobile({ selectedProductRecommendation })}
                {this.renderDesktop({ productRecommendations })}
            </div>
        );
    }
}
